import {
  QUESTION_CONTENT_VERSION_HEADER,
  QUESTION_CONTENT_VERSION_HEADER_VALUE,
} from '@shared/questionContentVersion';
import { setServerDown } from '@/store/serverStatusStore';
import {
  runWithDevLoadingTrace,
  toDevLoadingTraceKey,
} from '@/utils/devLoadingTrace';

import {
  adaptiveBudgetUnavailableMessage,
  adaptiveEgressMessage,
  isAdaptiveBudgetUnavailableError,
} from './adaptiveEgressError';
import {
  isAuthBridgeRegistered,
  refreshAuthToken,
  triggerReauthRedirect,
} from './authBridge';
import { captureException } from './sentryService';

/**
 * Every request builds its target as `${API_URL}${endpoint}`, so an unusable
 * base URL does not surface as a handled API error — it throws out of `fetch()`
 * itself and reaches users as a raw browser string (Safari: "The string did not
 * match the expected pattern."). Runtime brand config decides this value at
 * boot, so validate it here rather than trusting the source.
 *
 * Accepts absolute http(s) URLs and root-relative paths ('/api'). Returns the
 * normalized base, or null when the value is unusable.
 */
const normalizeApiBaseUrl = (candidate: unknown): string | null => {
  if (typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('/')) {
    // '//host' is protocol-relative, not a same-origin path.
    if (trimmed.startsWith('//')) return null;
  } else {
    try {
      const { protocol } = new URL(trimmed);
      if (protocol !== 'http:' && protocol !== 'https:') return null;
    } catch {
      return null;
    }
  }

  // Endpoints all begin with '/', so a trailing slash would yield '//'.
  return trimmed.replace(/\/+$/, '');
};

export let API_URL =
  normalizeApiBaseUrl(import.meta.env.VITE_API_URL) ??
  'http://localhost:5001/api';

export const setApiBaseUrl = (newBaseUrl: string): void => {
  const normalized = normalizeApiBaseUrl(newBaseUrl);

  if (normalized === null) {
    // Keep the last known-good base. Adopting a broken override would break
    // every subsequent request app-wide, which is far worse than ignoring it.
    captureException(
      new Error(
        `Rejected unusable API base URL: ${JSON.stringify(newBaseUrl)}`
      ),
      { level: 'error', tags: { area: 'boot' } }
    );
    return;
  }

  API_URL = normalized;
};

const isIdempotent = (method: string): boolean =>
  !['POST', 'PATCH'].includes(method.toUpperCase());

const sleep = (ms: number, signal?: AbortSignal | null): Promise<void> =>
  new Promise((resolve, reject) => {
    signal?.throwIfAborted();
    const onAbort = () => {
      clearTimeout(timeout);
      reject(signal!.reason);
    };
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });

const isAbortError = (error: unknown): boolean =>
  error != null &&
  typeof error === 'object' &&
  'name' in error &&
  error.name === 'AbortError';

const traceEndpoint = (endpoint: string): string =>
  endpoint
    .replace(/auth0(%7C|\|)[^/?#]+/gi, 'auth0$1...')
    .replace(/\/practice\/questions\/[^/?#]+/gi, '/practice/questions/...')
    .replace(
      /\/practice\/session\/adaptive\/[^/?#]+/gi,
      '/practice/session/adaptive/...'
    );

const traceHttpKey = (method: string, endpoint: string): string => {
  const path = endpoint.split('?')[0] || endpoint;
  return `http.${method.toLowerCase()}.${toDevLoadingTraceKey(path) || 'request'}`;
};

/**
 * Maintenance mode is app-wide and total — `setServerDown(true)` swaps whatever
 * the user was mid-way through for MaintenancePage — so a single 5xx is not
 * enough evidence for it. Both restarts we hit routinely answer /health with a
 * 5xx for a second or two: express listens before boot finishes (503
 * `{reason:'boot'}`) and nginx has no upstream mid blue-green promotion (502).
 * Confirm across a window instead. A real outage still shows maintenance, just
 * ~12s later.
 *
 * Every 5xx gets the same window rather than only the boot-shaped body: the
 * proxy 502 carries no such marker, and deferring the flip costs a handful of
 * cheap probes while skipping it would cost a real outage its maintenance page.
 */
const HEALTH_GRACE_BUDGET_MS = 12_000;
const HEALTH_GRACE_POLL_MS = 1_000;
/**
 * Once down, nothing else can clear the flag: `useAuth` short-circuits while
 * the server is down and MaintenancePage issues no requests, so recovery used
 * to depend on some unrelated request happening to succeed. The watcher owns
 * both directions — every `setServerDown(true)` is followed by this poll.
 */
const HEALTH_RECOVERY_POLL_MS = 2_000;

type HealthProbe = 'ok' | 'server-error' | 'inconclusive';

/**
 * `inconclusive` covers a browser-side network miss and any non-5xx response.
 * Neither proves the server is in maintenance — the user's own connection is
 * the likelier culprit, and mid-restart the listening socket is simply gone —
 * so the caller keeps the user where they are.
 *
 * @param quiet the watcher probes on a loop; only the first probe logs.
 */
const probeServerHealth = async (quiet = false): Promise<HealthProbe> => {
  try {
    const response = await fetch(`${API_URL}/health`, {
      mode: 'cors',
      credentials: 'include',
      cache: 'no-store',
    });

    if (response.ok) return 'ok';
    return response.status >= 500 ? 'server-error' : 'inconclusive';
  } catch (healthCheckError) {
    if (!quiet) console.error('Health check failed:', healthCheckError);
    return 'inconclusive';
  }
};

let healthWatchRunning = false;
let healthWatchGeneration = 0;

/**
 * Test-only. The recovery poll runs until the server answers, so a suite that
 * drives the watcher into a down state would otherwise leak a live loop into
 * the next test (against a restored `fetch`).
 */
export const resetServerHealthWatchForTests = (): void => {
  healthWatchGeneration += 1;
  healthWatchRunning = false;
};

const watchServerHealth = async (): Promise<void> => {
  if (healthWatchRunning) return;
  healthWatchRunning = true;
  const generation = healthWatchGeneration;
  const isCurrent = () => generation === healthWatchGeneration;

  try {
    const graceDeadline = Date.now() + HEALTH_GRACE_BUDGET_MS;

    // Grace window: the app stays on screen while we find out whether this is
    // a restart or an outage. Only a clean /health ends it early.
    while (Date.now() < graceDeadline) {
      await sleep(HEALTH_GRACE_POLL_MS);
      const probe = await probeServerHealth(true);
      if (!isCurrent()) return;
      if (probe === 'ok') {
        setServerDown(false);
        return;
      }
    }

    setServerDown(true);

    for (;;) {
      await sleep(HEALTH_RECOVERY_POLL_MS);
      const probe = await probeServerHealth(true);
      if (!isCurrent()) return;
      if (probe === 'ok') {
        setServerDown(false);
        return;
      }
    }
  } finally {
    // A reset already handed ownership to whatever runs next.
    if (isCurrent()) healthWatchRunning = false;
  }
};

const checkServerHealth = async (): Promise<void> => {
  const probe = await probeServerHealth();

  if (probe === 'ok') {
    setServerDown(false);
    return;
  }

  // Deliberately not awaited: the request that triggered this has already
  // exhausted its retries and must reject now, while the watcher outlives it.
  if (probe === 'server-error') void watchServerHealth();
};

interface ApiError extends Error {
  status?: number;
  isClientError?: boolean;
  /**
   * Set when retrying cannot possibly help — rate limits and quota rejections.
   * Separate from `isClientError` because that flag is also read by callers
   * (classCodesApi) to mean "a 4xx the server explained", and a fail-closed
   * 503 is neither a 4xx nor explained.
   */
  isNonRetryable?: boolean;
  code?: string;
  [key: string]: unknown;
}

/**
 * Thrown after the 401 interceptor has exhausted recovery (token refresh failed
 * or the replay still 401'd) and kicked off a login redirect. It is
 * deliberately NOT a 401 `ApiError` so `useAuthenticatedCall` and other
 * callsites don't re-run their own refresh/redirect on top of the global one —
 * the user is already being sent to login. Callers can surface `message` (a
 * transient banner before navigation) or ignore it.
 */
export class SessionExpiredError extends Error {
  readonly isSessionExpired = true;
  constructor(
    message = 'Your session has expired. Redirecting you to sign in…'
  ) {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

interface FetchOptions extends RequestInit {
  headers?: Record<string, string>;
  /**
   * Optional Auth0 bearer token. When provided, `fetchWithConfig` attaches
   * `Authorization: Bearer <token>` without clobbering any explicit header.
   * Prefer this over hand-rolling the header at each callsite so the token
   * path is centralised.
   */
  authToken?: string;
  /**
   * Disable automatic replay for non-idempotent writes that have no
   * server-side idempotency key. The default keeps the existing retry policy.
   */
  retry?: boolean;
}

type ResponseParser<T> = (response: Response) => Promise<T>;

export interface DownloadResponse {
  blob: Blob;
  /** Filename supplied by Content-Disposition, when the server provided one. */
  filename: string | null;
}

const contentDispositionFilename = (header: string | null): string | null => {
  if (!header) return null;

  // Prefer RFC 5987's UTF-8 form when both forms are present. The export route
  // currently emits a quoted ASCII filename, but this keeps the shared helper
  // useful for future downloads whose names contain spaces or non-ASCII text.
  const encoded = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i)?.[1];
  if (encoded) {
    const value = encoded.trim().replace(/^"|"$/g, '');
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  const plain = header.match(/filename\s*=\s*(?:"([^"]*)"|([^;]*))/i);
  const value = (plain?.[1] ?? plain?.[2])?.trim();
  return value || null;
};

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  if (response.status === 204 || response.status === 205) {
    return null as T;
  }
  return (await response.json()) as T;
};

const parseDownloadResponse = async (
  response: Response
): Promise<DownloadResponse> => ({
  blob: await response.blob(),
  filename: contentDispositionFilename(
    response.headers.get('Content-Disposition')
  ),
});

const fetchWithRetry = async <T = unknown>(
  endpoint: string,
  options: FetchOptions = {},
  retries: number,
  parseResponse: ResponseParser<T>
): Promise<T> => {
  const { method = 'GET' } = options;

  for (let i = 0; i < retries; i++) {
    try {
      options.signal?.throwIfAborted();
      if (i > 0) {
        const retryDelayMs = 1000 * i;
        await runWithDevLoadingTrace(
          `http.retry-wait.${toDevLoadingTraceKey(endpoint) || 'request'}`,
          () => sleep(retryDelayMs, options.signal),
          {
            kind: 'http-retry-wait',
            method: method.toUpperCase(),
            endpoint: traceEndpoint(endpoint),
            completedAttempts: i,
            maxAttempts: retries,
            retryDelayMs,
            idempotent: isIdempotent(method),
          }
        );
      }
      let responseStatus: number | null = null;
      let responseOk: boolean | null = null;
      const traceKey = traceHttpKey(method, endpoint);
      const response = await runWithDevLoadingTrace(
        traceKey,
        async () => {
          const nextResponse = await fetch(`${API_URL}${endpoint}`, {
            mode: 'cors',
            credentials: 'include',
            // Skip the browser HTTP cache. iOS Safari intermittently
            // rehydrates a 304-revalidated cache entry as a 200 with an empty
            // body, which rejects response.json() on every retry ("The string
            // did not match the expected pattern"). The server also sends
            // Cache-Control: no-store; this covers caches poisoned before that
            // header shipped and any proxy that strips it.
            cache: 'no-store',
            ...options,
          });
          responseStatus = nextResponse.status;
          responseOk = nextResponse.ok;
          return nextResponse;
        },
        () => ({
          kind: 'http',
          method: method.toUpperCase(),
          endpoint: traceEndpoint(endpoint),
          attempt: i + 1,
          maxAttempts: retries,
          status: responseStatus,
          ok: responseOk,
          idempotent: isIdempotent(method),
          hasAuthHeader: Boolean(options.headers?.Authorization),
        })
      );

      if (!response.ok) {
        const errorData = await response.json().catch((error: unknown) => {
          // A cancelled body read is not a malformed error response.
          if (options.signal?.aborted || isAbortError(error)) throw error;
          return {};
        });
        if (response.status >= 500) {
          // Read the body on every 5xx. The two codes below change retry
          // policy — a budget store that is down cannot tell an exhausted actor
          // from a healthy one, so hammering it is the one thing that makes it
          // worse, and a setup error needs intervention rather than another
          // attempt — and other structured failures still carry application
          // state a caller must not lose. A destructive request can partially
          // succeed before its upstream dependency refuses the final step (for
          // example, Zoom passcode rotation succeeds and END fails); replacing
          // `{code, message, rotated:true}` with `Server error: 502` invites the
          // user to repeat the destructive half they cannot see happened. A
          // bodyless 5xx still falls through to `Server error: NNN`, which is
          // what the Sentry filter matches on.
          if (errorData?.code === 'LOCAL_REPLICA_SET_REQUIRED') {
            throw Object.assign(new Error(errorData.message), {
              status: response.status,
              code: errorData.code,
              isNonRetryable: true,
            });
          }
          if (isAdaptiveBudgetUnavailableError(errorData)) {
            const error = new Error(
              adaptiveBudgetUnavailableMessage()
            ) as ApiError;
            Object.assign(error, {
              ...errorData,
              status: response.status,
              isNonRetryable: true,
            });
            throw error;
          }
          const structured =
            errorData && typeof errorData === 'object'
              ? (errorData as Record<string, unknown>)
              : {};
          const message =
            (typeof structured.message === 'string' && structured.message) ||
            (typeof structured.error === 'string' && structured.error) ||
            `Server error: ${response.status}`;
          const error = new Error(message) as ApiError;
          Object.assign(error, structured, { status: response.status });
          throw error;
        }
        if (response.status === 429) {
          // The daily-allowance body carries no `message`, so without this
          // branch the raw wire code would reach the user as copy.
          const rateLimitMessage =
            adaptiveEgressMessage(errorData) ||
            errorData.message ||
            errorData.error ||
            "You've submitted too many requests. Please wait a few minutes before trying again.";
          const error = new Error(rateLimitMessage) as ApiError;
          // No 429 is retryable: a burst limit needs a full window to drain and
          // a daily allowance needs a new SGT day. Retrying either just spends
          // the caller's second attempt on a guaranteed second rejection.
          Object.assign(error, {
            ...errorData,
            status: 429,
            isNonRetryable: true,
          });
          throw error;
        }
        const error = new Error(
          errorData.message ||
            errorData.error ||
            `API request failed: ${response.statusText}`
        ) as ApiError;
        Object.assign(error, {
          ...errorData,
          status: response.status,
          isClientError: true,
        });
        throw error;
      }

      setServerDown(false);
      return await parseResponse(response);
    } catch (error) {
      // Remounts/navigation cancel requests deliberately. Preserve the original
      // rejection, including custom signal reasons, without retrying or probing
      // server health. The same rule covers cancellation during retry backoff.
      if (options.signal?.aborted || isAbortError(error)) {
        // eslint-disable-next-line no-console -- Expected cancellation belongs at debug level.
        console.debug('API request cancelled:', {
          endpoint: traceEndpoint(endpoint),
        });
        throw error;
      }
      const apiError = error as ApiError;

      if (apiError.isClientError || apiError.isNonRetryable) {
        throw apiError;
      }

      if (
        i < retries - 1 &&
        (apiError.message.includes('Network') || isIdempotent(method))
      ) {
        continue;
      }

      await checkServerHealth();

      console.error('API Error after retries:', { endpoint, error });
      throw error;
    }
  }

  throw new Error('Retry loop exhausted without returning');
};

const fetchWithResponseParser = async <T>(
  endpoint: string,
  options: FetchOptions,
  accept: string,
  parseResponse: ResponseParser<T>
): Promise<T> => {
  const {
    authToken,
    headers: providedHeaders,
    retry = true,
    ...rest
  } = options;
  const maxAttempts = retry ? 2 : 1;
  const headers: Record<string, string> = {
    Accept: accept,
    [QUESTION_CONTENT_VERSION_HEADER]: QUESTION_CONTENT_VERSION_HEADER_VALUE,
    ...providedHeaders,
  };

  if (rest.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  if (authToken && !headers.Authorization) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  try {
    return await fetchWithRetry<T>(
      endpoint,
      { ...rest, headers },
      maxAttempts,
      parseResponse
    );
  } catch (error) {
    const apiError = error as ApiError;

    // Global re-auth safety net. A 401 on an authenticated request means the
    // bearer token failed verification — almost always an expired ID token on a
    // long-lived SPA session. The server rejects at the auth middleware BEFORE
    // running the route handler, so no write has happened and replaying a
    // POST/PATCH is safe. Force-refresh the token once and replay; if the
    // session is genuinely dead, bounce to login (preserving the current path)
    // so no surface silently dead-ends on an error banner.
    //
    // Only engage for requests that actually carried a bearer and only when an
    // auth bridge is registered — otherwise fall through to the original error
    // (unauthenticated calls, tests, prerender) and preserve legacy behaviour.
    if (
      apiError.status !== 401 ||
      !headers.Authorization ||
      !isAuthBridgeRegistered()
    ) {
      throw error;
    }

    const staleToken = headers.Authorization.slice('Bearer '.length);
    const freshToken = await refreshAuthToken();

    if (freshToken && freshToken !== staleToken) {
      try {
        return await fetchWithRetry<T>(
          endpoint,
          {
            ...rest,
            headers: { ...headers, Authorization: `Bearer ${freshToken}` },
          },
          maxAttempts,
          parseResponse
        );
      } catch (retryError) {
        // A non-401 on replay is a real error (e.g. 403/404) — surface it as-is.
        if ((retryError as ApiError).status !== 401) throw retryError;
        // Still 401 with a freshly-minted token → the session is unrecoverable.
      }
    }

    triggerReauthRedirect();
    throw new SessionExpiredError();
  }
};

/**
 * Reject a missing or non-string bearer token before it reaches the network.
 *
 * Every authenticated service used to carry its own copy of this guard (ten
 * near-identical definitions plus eleven inlined in `enrollmentsAdminApi`),
 * which let three different messages drift apart for one condition. It lives
 * beside `fetchWithConfig` because that is the only thing the token is for.
 *
 * Declared as a function (not an arrow) so TypeScript accepts it as an
 * assertion when imported.
 */
export function requireToken(token: unknown): asserts token is string {
  if (!token || typeof token !== 'string') {
    throw new Error('Authentication token is required');
  }
}

/**
 * The bearer and content-version headers `fetchWithConfig` sends, for the one
 * transport it cannot carry: an XMLHttpRequest upload that reports progress
 * (fetch has no upload progress). The bearer is still built only here.
 */
export const authenticatedRequestHeaders = (
  authToken: string
): Record<string, string> => ({
  [QUESTION_CONTENT_VERSION_HEADER]: QUESTION_CONTENT_VERSION_HEADER_VALUE,
  Authorization: `Bearer ${authToken}`,
});

export const fetchWithConfig = async <T = unknown>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> =>
  fetchWithResponseParser(endpoint, options, 'application/json', response =>
    parseJsonResponse<T>(response)
  );

/** Accept header for workbook downloads — the historical default. */
export const XLSX_ACCEPT =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Accept header for document downloads (receipts, tutor statements). */
export const PDF_ACCEPT = 'application/pdf';

export interface DownloadOptions extends FetchOptions {
  /**
   * What the caller will accept back. Defaults to {@link XLSX_ACCEPT} because
   * the workbook export came first; PDF surfaces pass {@link PDF_ACCEPT}.
   *
   * Never reaches `fetch` as an init key — it is stripped below and turned
   * into the `Accept` header, which is the only thing it means.
   */
  accept?: string;
}

/**
 * Fetch an authenticated binary response through the same retry, health, and
 * 401 refresh/replay path as JSON API calls. Error responses are still parsed
 * as JSON by `fetchWithRetry` — that happens before the response parser ever
 * runs — so a 400/404/409 reaches the caller as `{ status, code, message }`
 * whatever the Accept header asked for, never as an opaque Blob.
 */
export const fetchDownloadWithConfig = async (
  endpoint: string,
  options: DownloadOptions = {}
): Promise<DownloadResponse> => {
  const { accept = XLSX_ACCEPT, ...fetchOptions } = options;
  return fetchWithResponseParser(
    endpoint,
    fetchOptions,
    accept,
    parseDownloadResponse
  );
};

/**
 * The server's machine-readable refusal code, if this error carries one.
 *
 * `fetchWithRetry` copies the whole JSON error body onto the thrown Error, so
 * a route that answers `{ code: 'receipt_not_available', message }` surfaces
 * here as `err.code`. Callers branch on the code and render the server's own
 * message — never a code, and never a message they made up.
 */
export const apiErrorCode = (err: unknown): string | null => {
  const code = (err as ApiError | null | undefined)?.code;
  return typeof code === 'string' && code.length > 0 ? code : null;
};

/**
 * Fetch private binary content through the common auth-refresh/error path.
 * Unlike `fetchDownloadWithConfig`, this is for inline media and therefore
 * does not imply an XLSX Accept header or parse Content-Disposition.
 */
export const fetchBlobWithConfig = async (
  endpoint: string,
  options: FetchOptions = {}
): Promise<Blob> =>
  fetchWithResponseParser(
    endpoint,
    options,
    'image/png, image/jpeg, image/webp',
    response => response.blob()
  );
